/**
 * POI MBC — vue cartographique du référentiel `stops` (mêmes objets que la page Arrêts).
 * Source unique : /api/stops. Clic droit → « Ajouter un POI ici ». Sélection → fiche
 * (nom, coordonnées, Modifier, Déplacer le POI → drag → confirmation → sauvegarde).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MapPin, Plus, Pencil, Trash2, Power, RefreshCw, Move, X, List, FileSpreadsheet, AlertTriangle, Share2, GitMerge, Download, Route, Unlink, Eye, EyeOff, Tags } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { downloadReferentialExport } from "@/lib/downloadReferential";
import { PageShell, PageHeader, SectionCard, SearchBar, ActionButton, StatusBadge, EmptyState } from "@/components/ds";
import StopsReferentialMap from "@/components/stops/StopsReferentialMap";
import StopFormModal from "@/components/stops/StopFormModal";
import ConfirmDeleteDialog from "@/components/stops/ConfirmDeleteDialog";
import StopsImportDialog from "@/components/stops/StopsImportDialog";
import StopMergeDialog from "@/components/stops/StopMergeDialog";
import StopsCategorizeDialog from "@/components/stops/StopsCategorizeDialog";
import { formatCoord, formatKm, formatMinutes } from "@/lib/stopsUnits";
import { styleOf, markerHtml, CATEGORIES } from "@/lib/stopStyles";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

function errText(e) {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object" && !Array.isArray(d) && d.message) return d.message;
  return formatApiErrorDetail(d);
}

export default function AdminPoiMbc() {
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [ctx, setCtx] = useState(null); // { lat, lng, x, y }
  const [form, setForm] = useState({ open: false, initial: null });
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [moving, setMoving] = useState(false);
  const [pendingMove, setPendingMove] = useState(null); // { stop, lat, lng }
  const [revertNonce, setRevertNonce] = useState(0);
  const [importOpen, setImportOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [links, setLinks] = useState([]);
  const [suggestions, setSuggestions] = useState({});
  const [categorizeOpen, setCategorizeOpen] = useState(false);
  const [dismissed, setDismissed] = useState(new Set());
  const [showLinks, setShowLinks] = useState(true);
  const [selectedLinkId, setSelectedLinkId] = useState(null);
  const [hiddenCats, setHiddenCats] = useState(new Set());
  const [onlyUnlinked, setOnlyUnlinked] = useState(false);
  const [pendingResolve, setPendingResolve] = useState(null); // { stop, candidate }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, l, sg] = await Promise.all([api.get("/stops"), api.get("/inter-stops"), api.get("/stops/suggestions")]);
      setStops(r.data?.items || []);
      setLinks(l.data?.items || []);
      setSuggestions(Object.fromEntries((sg.data?.items || []).map((i) => [i.stop_id, i])));
    } catch (e) { toast.error(errText(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const selected = useMemo(() => stops.find((s) => s.id === selectedId) || null, [stops, selectedId]);
  const activeLinks = useMemo(() => links.filter((l) => l.active !== false), [links]);
  const linkedIds = useMemo(() => new Set(activeLinks.flatMap((l) => [l.origin_stop_id, l.destination_stop_id])), [activeLinks]);
  const isUnlinked = (s) => s.active !== false && !linkedIds.has(s.id);
  const categoriesPresent = useMemo(() => {
    const m = new Map();
    stops.forEach((s) => { const st = styleOf(s); const e = m.get(st.category) || { ...st, count: 0 }; e.count += 1; m.set(st.category, e); });
    return [...m.values()].sort((a, b) => b.count - a.count);
  }, [stops]);
  const visibleStops = useMemo(() => stops.filter((s) => !hiddenCats.has(styleOf(s).category) && (!onlyUnlinked || isUnlinked(s))), [stops, hiddenCats, onlyUnlinked, linkedIds]); // eslint-disable-line react-hooks/exhaustive-deps
  const visibleLinks = useMemo(() => (showLinks ? links.filter((l) => l.origin_stop && l.destination_stop) : []), [links, showLinks]);
  const selectedLink = useMemo(() => links.find((l) => l.id === selectedLinkId) || null, [links, selectedLinkId]);
  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    return visibleStops.filter((s) => !n || s.name.toLowerCase().includes(n));
  }, [visibleStops, q]);
  const toggleCat = (k) => setHiddenCats((p) => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const upsertLocal = (doc) => setStops((prev) => {
    const next = prev.some((s) => s.id === doc.id) ? prev.map((s) => (s.id === doc.id ? doc : s)) : [...prev, doc];
    return next.sort((a, b) => a.name.localeCompare(b.name, "fr"));
  });

  const submitForm = async (payload) => {
    setSaving(true);
    try {
      if (form.initial?.id) {
        const r = await api.patch(`/stops/${form.initial.id}`, payload);
        upsertLocal(r.data); toast.success("POI modifié");
      } else {
        const r = await api.post("/stops", { ...payload, source: form.initial?.source || "MANUAL" });
        upsertLocal(r.data); setSelectedId(r.data.id); toast.success("POI créé");
      }
      setForm({ open: false, initial: null });
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (stop) => {
    try {
      const r = await api.patch(`/stops/${stop.id}`, { active: !stop.active });
      upsertLocal(r.data); toast.success(r.data.active ? "POI réactivé" : "POI désactivé");
    } catch (e) { toast.error(errText(e)); }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/stops/${toDelete.id}`);
      setStops((prev) => prev.filter((s) => s.id !== toDelete.id));
      if (selectedId === toDelete.id) setSelectedId(null);
      toast.success("POI supprimé");
    } catch (e) { toast.error(errText(e), { duration: 6000 }); }
    finally { setDeleting(false); setToDelete(null); }
  };

  const confirmMove = async () => {
    const { stop, lat, lng } = pendingMove;
    try {
      const r = await api.patch(`/stops/${stop.id}`, { latitude: +lat.toFixed(6), longitude: +lng.toFixed(6) });
      upsertLocal(r.data); toast.success(`« ${stop.name} » déplacé`);
    } catch (e) { toast.error(errText(e)); setRevertNonce((n) => n + 1); }
    finally { setPendingMove(null); setMoving(false); }
  };
  const cancelMove = () => { setPendingMove(null); setRevertNonce((n) => n + 1); };

  const applySuggestion = async (stop, category, source) => {
    try {
      const r = await api.patch(`/stops/${stop.id}`, { category, category_source: source });
      upsertLocal(r.data); setSuggestions((p) => { const n = { ...p }; delete n[stop.id]; return n; });
      toast.success(`Catégorie « ${CATEGORIES.find((c) => c.key === category)?.label || category} » confirmée`);
    } catch (e) { toast.error(errText(e)); }
  };
  const toCategorize = Object.keys(suggestions).length;

  const hasConflict = (s) => s?.coord_conflict?.status === "pending";
  const conflictCount = useMemo(() => stops.filter(hasConflict).length, [stops]);
  const candidateMarkers = useMemo(() => {
    if (!resolving || !hasConflict(selected)) return [];
    return (selected.coord_conflict.candidates || []).map((c, i) => ({
      id: `${selected.id}-${i}`, latitude: c.latitude, longitude: c.longitude,
      label: String.fromCharCode(65 + i), color: i === 0 ? "#7C3AED" : "#DB2777",
      tooltip: `${String.fromCharCode(65 + i)} · ${c.latitude}, ${c.longitude}${c.row ? ` · ligne ${c.row}` : ""}${c.extra ? " · " + Object.values(c.extra).join(" ") : ""}`,
      onClick: () => setPendingResolve({ stop: selected, candidate: c, label: String.fromCharCode(65 + i) }),
    }));
  }, [resolving, selected]);

  const confirmResolve = async () => {
    const { stop, candidate } = pendingResolve;
    try {
      const r = await api.post(`/stops/${stop.id}/resolve-coords`, { latitude: candidate.latitude, longitude: candidate.longitude });
      upsertLocal(r.data); toast.success(`Coordonnées de « ${stop.name} » validées`);
      setResolving(false);
    } catch (e) { toast.error(errText(e)); }
    finally { setPendingResolve(null); }
  };

  return (
    <PageShell testId="admin-poi-mbc-page">
      <PageHeader
        eyebrow="Planification"
        title="POI MBC"
        subtitle="Vue cartographique du référentiel des arrêts MBC — mêmes objets que la page Arrêts, gérés localement dans LOGIBUS (aucune synchronisation Navixy)."
        actions={
          <>
            <ActionButton variant="ghost" icon={RefreshCw} onClick={load} loading={loading} testId="poi-refresh">Actualiser</ActionButton>
            <ActionButton variant="ghost" icon={List} to="/admin/stops" testId="poi-goto-stops">Vue tableau</ActionButton>
            <ActionButton variant="ghost" icon={FileSpreadsheet} onClick={() => setImportOpen(true)} testId="poi-import-btn">Importer Excel</ActionButton>
            <ActionButton variant="ghost" icon={Tags} onClick={() => setCategorizeOpen(true)} testId="poi-categorize-btn">POI à catégoriser ({toCategorize})</ActionButton>
            <ActionButton variant="ghost" icon={Download} onClick={downloadReferentialExport} testId="poi-export-btn">Exporter Excel</ActionButton>
            <ActionButton variant="primary" icon={Plus} onClick={() => setForm({ open: true, initial: null })} testId="poi-add-btn">Ajouter un POI</ActionButton>
          </>
        }
      />

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
        <SectionCard title="Carte des POI" icon={MapPin} tone="navy" className="xl:col-span-3" bodyClassName="h-[620px]"
          actions={<span className="text-[10px] font-mono opacity-70" data-testid="poi-map-count">{stops.length} POI{conflictCount ? ` · ${conflictCount} à valider` : ""}</span>}>
          <StopsReferentialMap
            stops={visibleStops}
            links={visibleLinks}
            selectedLinkId={selectedLinkId}
            onLinkClick={(l) => setSelectedLinkId(l.id)}
            focusStopId={selectedId}
            draggableStopId={moving && selected ? selected.id : null}
            revertNonce={revertNonce}
            onContextMenu={(lat, lng, pt) => { setCtx({ lat, lng, ...pt }); }}
            onMapClick={() => setCtx(null)}
            onMarkerClick={(s) => { if (!moving) { setSelectedId(s.id); setCtx(null); setResolving(false); } }}
            onMarkerDragEnd={(s, lat, lng) => setPendingMove({ stop: s, lat, lng })}
            extraMarkers={candidateMarkers}
            hint={moving ? "Glissez le marqueur orange puis confirmez" : resolving ? "Cliquez sur le candidat A ou B pour choisir la bonne position" : "Clic droit sur la carte → « Ajouter un POI ici »"}
            testId="poi-map"
          >
            <div className="absolute top-2 right-2 z-[400] flex flex-col gap-1 items-end" data-testid="poi-map-toolbar">
              <button type="button" data-testid="poi-toggle-links" onClick={() => setShowLinks((v) => !v)} disabled={links.length === 0}
                title={links.length === 0 ? "Aucun inter-arrêt enregistré" : showLinks ? "Masquer les liaisons" : "Afficher les liaisons"}
                className={`flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-widest border border-zinc-900 ${links.length === 0 ? "bg-zinc-100 text-zinc-400 cursor-not-allowed" : showLinks ? "bg-zinc-900 text-[#FFC60A]" : "bg-white"}`}>
                {showLinks ? <Eye size={11} /> : <EyeOff size={11} />} Liaisons {links.length}
              </button>
              <button type="button" data-testid="poi-toggle-unlinked" onClick={() => setOnlyUnlinked((v) => !v)} disabled={activeLinks.length === 0}
                title={activeLinks.length === 0 ? "Filtre disponible dès qu'un inter-arrêt actif existe" : "Arrêts actifs sans aucune liaison"}
                className={`flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-widest border border-zinc-900 ${activeLinks.length === 0 ? "bg-zinc-100 text-zinc-400 cursor-not-allowed" : onlyUnlinked ? "bg-orange-600 text-white" : "bg-white"}`}>
                <Unlink size={11} /> Arrêts non reliés{activeLinks.length ? ` ${stops.filter(isUnlinked).length}` : ""}
              </button>
            </div>
            {categoriesPresent.length > 0 && (
              <div className="absolute bottom-8 right-2 z-[400] bg-white/95 border border-zinc-900 p-1.5 space-y-0.5 max-h-[45%] overflow-auto" data-testid="poi-legend">
                <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 px-1">Légende · cliquer pour masquer</div>
                {categoriesPresent.map((c) => (
                  <button key={c.category} type="button" data-testid={`poi-legend-${c.category}`} onClick={() => toggleCat(c.category)}
                    className={`w-full flex items-center gap-2 px-1 py-0.5 text-[11px] hover:bg-zinc-100 ${hiddenCats.has(c.category) ? "opacity-40 line-through" : ""}`}>
                    <span dangerouslySetInnerHTML={{ __html: markerHtml({ color: c.color, icon: c.icon, size: 18 }) }} />
                    <span className="flex-1 text-left">{c.categoryLabel}</span>
                    <span className="font-mono text-zinc-500">{c.count}</span>
                  </button>
                ))}
              </div>
            )}
            {selectedLink && (
              <div className="absolute top-2 left-2 z-[400] bg-white border border-zinc-900 px-3 py-2 text-xs shadow-[4px_4px_0_0_#000]" data-testid="poi-link-detail">
                <div className="flex items-center gap-2 font-bold"><Route size={12} /> {selectedLink.origin_stop?.name} → {selectedLink.destination_stop?.name}
                  <button type="button" onClick={() => setSelectedLinkId(null)} data-testid="poi-link-detail-close"><X size={11} /></button></div>
                <div className="font-mono text-zinc-600">{formatKm(selectedLink.distance_meters)} · {formatMinutes(selectedLink.travel_time_seconds)}{selectedLink.active === false ? " · inactif" : ""}</div>
              </div>
            )}
            {ctx && (
              <div className="absolute z-[500] bg-white border border-zinc-900 shadow-[4px_4px_0_0_#000]" style={{ left: ctx.x, top: ctx.y }} data-testid="poi-context-menu">
                <button type="button" data-testid="poi-context-add"
                  onClick={() => { setForm({ open: true, initial: { latitude: +ctx.lat.toFixed(6), longitude: +ctx.lng.toFixed(6), source: "MAP" } }); setCtx(null); }}
                  className="flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[#FFC60A]">
                  <Plus size={12} /> Ajouter un POI ici
                </button>
                <div className="px-3 pb-2 text-[10px] font-mono text-zinc-500">{ctx.lat.toFixed(5)}, {ctx.lng.toFixed(5)}</div>
              </div>
            )}
          </StopsReferentialMap>
        </SectionCard>

        <div className="space-y-4">
          <SectionCard title={selected ? "POI sélectionné" : "Aucune sélection"} icon={MapPin} tone={selected ? "yellow" : "white"} padded testId="poi-detail-panel"
            actions={selected && <button type="button" onClick={() => { setSelectedId(null); setMoving(false); }} data-testid="poi-detail-close"><X size={12} /></button>}>
            {selected ? (
              <div className="space-y-3">
                <div>
                  <div className="font-display font-bold text-lg leading-tight" data-testid="poi-detail-name">{selected.name}</div>
                  <div className="font-mono text-xs text-zinc-600" data-testid="poi-detail-coords">{formatCoord(selected.latitude)}, {formatCoord(selected.longitude)}</div>
                  {selected.address && <div className="text-xs text-zinc-600 mt-1">{selected.address}</div>}
                  <div className="mt-2 flex gap-1 flex-wrap">
                    <StatusBadge tone={selected.active ? "success" : "neutral"} label={selected.active ? "Actif" : "Inactif"} size="sm" />
                    {selected.source && <StatusBadge tone="muted" label={selected.source} size="sm" withDot={false} />}
                    {selected.flags?.includes("shared_coords") && <StatusBadge tone="info" label="Coordonnées partagées" size="sm" withDot={false} testId="poi-detail-shared" />}
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest" data-testid="poi-detail-category">
                      <span dangerouslySetInnerHTML={{ __html: markerHtml({ ...styleOf(selected), size: 16 }) }} /> {styleOf(selected).categoryLabel}
                    </span>
                  </div>
                  {selected.aliases?.length > 0 && <div className="text-[11px] text-zinc-500 mt-1" data-testid="poi-detail-aliases">Alias : {selected.aliases.join(" · ")}</div>}
                </div>
                {selected.flags?.includes("shared_coords") && (
                  <div className="border border-blue-300 bg-blue-50 p-2 text-xs space-y-1" data-testid="poi-detail-shared-box">
                    <div>Autre(s) POI aux mêmes coordonnées : {stops.filter((s) => s.id !== selected.id && s.latitude === selected.latitude && s.longitude === selected.longitude).map((s) => s.name).join(", ") || "—"}</div>
                    <ActionButton size="sm" variant="ghost" icon={GitMerge} testId="poi-detail-merge" onClick={() => setMergeOpen(true)}>Fusionner (avec confirmation)</ActionButton>
                  </div>
                )}
                {!selected.category && !dismissed.has(selected.id) && (
                  <div className="border border-zinc-300 bg-zinc-50 p-2 text-xs space-y-1.5" data-testid="poi-detail-suggestion">
                    <div><span className="font-mono uppercase tracking-widest text-zinc-500">Catégorie actuelle</span> : Autre (par défaut)</div>
                    {suggestions[selected.id]?.suggestion ? (
                      <>
                        <div><span className="font-mono uppercase tracking-widest text-zinc-500">Suggestion</span> : <b data-testid="poi-suggestion-category">{CATEGORIES.find((c) => c.key === suggestions[selected.id].suggestion)?.label}</b>
                          <span className="text-zinc-500"> · {suggestions[selected.id].reason}</span></div>
                        <div className="flex flex-wrap gap-1">
                          <ActionButton size="sm" variant="primary" testId="poi-suggestion-apply" onClick={() => applySuggestion(selected, suggestions[selected.id].suggestion, "USER_CONFIRMED_SUGGESTION")}>Appliquer la suggestion</ActionButton>
                          <ActionButton size="sm" variant="ghost" testId="poi-suggestion-other" onClick={() => setForm({ open: true, initial: selected })}>Choisir une autre catégorie</ActionButton>
                          <ActionButton size="sm" variant="subtle" testId="poi-suggestion-ignore" onClick={() => setDismissed((p) => new Set(p).add(selected.id))}>Ignorer</ActionButton>
                        </div>
                      </>
                    ) : (
                      <div className="text-zinc-500" data-testid="poi-suggestion-none">Aucune suggestion claire à partir du nom — <button type="button" className="underline" onClick={() => setForm({ open: true, initial: selected })} data-testid="poi-suggestion-choose">choisir manuellement</button></div>
                    )}
                  </div>
                )}
                {selected.category && selected.category_source && (
                  <div className="text-[10px] font-mono text-zinc-500" data-testid="poi-detail-category-source">Provenance catégorie : {selected.category_source === "USER_CONFIRMED_SUGGESTION" ? "suggestion confirmée" : "manuelle"}</div>
                )}
                {hasConflict(selected) && (
                  <div className="border border-orange-400 bg-orange-50 p-2 text-xs space-y-1" data-testid="poi-detail-conflict">
                    <div className="flex items-center gap-1 font-bold text-orange-800"><AlertTriangle size={12} /> Coordonnées à valider</div>
                    {selected.coord_conflict.candidates.map((c, i) => (
                      <div key={i} className="font-mono text-[11px]">{String.fromCharCode(65 + i)} · {c.latitude}, {c.longitude}{c.row ? ` · L${c.row}` : ""}{c.extra ? ` · ${Object.values(c.extra).join(" ")}` : ""}</div>
                    ))}
                    <ActionButton size="sm" variant={resolving ? "dark" : "primary"} icon={Share2} testId="poi-detail-resolve" onClick={() => { setResolving((r) => !r); setMoving(false); }}>
                      {resolving ? "Annuler la résolution" : "Résoudre sur la carte"}
                    </ActionButton>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-1.5">
                  <ActionButton size="sm" variant="ghost" icon={Pencil} testId="poi-detail-edit" onClick={() => setForm({ open: true, initial: selected })}>Modifier</ActionButton>
                  <ActionButton size="sm" variant={moving ? "dark" : "ghost"} icon={Move} testId="poi-detail-move" onClick={() => setMoving((m) => !m)}>
                    {moving ? "Annuler déplacement" : "Déplacer le POI"}
                  </ActionButton>
                  <ActionButton size="sm" variant="subtle" icon={Power} testId="poi-detail-toggle" onClick={() => toggleActive(selected)}>{selected.active ? "Désactiver" : "Activer"}</ActionButton>
                  <ActionButton size="sm" variant="danger" icon={Trash2} testId="poi-detail-delete" onClick={() => setToDelete(selected)}>Supprimer</ActionButton>
                </div>
                {moving && <p className="text-[11px] text-orange-700" data-testid="poi-move-hint">Glissez le marqueur sur la carte. Rien n'est enregistré sans votre confirmation.</p>}
              </div>
            ) : (
              <p className="text-xs text-zinc-500">Cliquez un marqueur ou une ligne de la liste. Clic droit sur la carte pour créer un POI à cet endroit.</p>
            )}
          </SectionCard>

          <SectionCard title="Liste des POI" icon={List} tone="white" actions={<span className="text-[10px] font-mono text-zinc-500" data-testid="poi-list-count">{filtered.length}/{stops.length}</span>}>
            {(hiddenCats.size > 0 || onlyUnlinked) && (
              <div className="px-3 py-1 text-[10px] font-mono text-orange-700 bg-orange-50 border-b border-orange-200" data-testid="poi-visual-filter-hint">
                Filtre visuel actif ({visibleStops.length}/{stops.length} affichés) — <button type="button" className="underline" onClick={() => { setHiddenCats(new Set()); setOnlyUnlinked(false); }} data-testid="poi-visual-filter-reset">réinitialiser</button>
              </div>
            )}
            <div className="p-2 border-b border-zinc-200">
              <SearchBar value={q} onChange={setQ} placeholder="Rechercher un POI…" testId="poi-search" dense />
            </div>
            {!loading && stops.length === 0 ? (
              <EmptyState icon={MapPin} title="Aucun POI" description="Ajoutez un POI, faites un clic droit sur la carte ou importez le fichier Excel MBC." dense />
            ) : (
              <ul className="max-h-[330px] overflow-auto divide-y divide-zinc-200" data-testid="poi-list">
                {filtered.map((s) => (
                  <li key={s.id}>
                    <button type="button" onClick={() => { setSelectedId(s.id); setMoving(false); }} data-testid={`poi-list-item-${s.id}`}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-zinc-50 ${s.id === selectedId ? "bg-[#FFF7CC]" : ""}`}>
                      <span className={`truncate flex items-center gap-1 ${s.active === false ? "text-zinc-400" : "font-medium"}`}>
                        {hasConflict(s) && <AlertTriangle size={11} className="text-orange-600 shrink-0" data-testid={`poi-list-conflict-${s.id}`} />}
                        <span className="shrink-0" dangerouslySetInnerHTML={{ __html: markerHtml({ ...styleOf(s), size: 14 }) }} />
                        {s.name}
                      </span>
                      <span className="font-mono text-[10px] text-zinc-500 shrink-0">{formatCoord(s.latitude)}, {formatCoord(s.longitude)}</span>
                    </button>
                  </li>
                ))}
                {filtered.length === 0 && <li className="p-4 text-xs text-zinc-500 text-center">Aucun POI ne correspond.</li>}
              </ul>
            )}
          </SectionCard>
        </div>
      </div>

      <StopFormModal open={form.open} initial={form.initial} saving={saving} onClose={() => setForm({ open: false, initial: null })} onSubmit={submitForm} />
      <StopsImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={load} />
      <StopsCategorizeDialog open={categorizeOpen} onClose={() => setCategorizeOpen(false)} onApplied={load} />
      <StopMergeDialog open={mergeOpen} onClose={() => setMergeOpen(false)} keep={selected} stops={stops}
        onMerged={(d) => { setStops((prev) => prev.filter((s) => s.id !== d.merged_id).map((s) => (s.id === d.kept.id ? d.kept : s))); }} />
      <ConfirmDeleteDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} loading={deleting} onConfirm={confirmDelete}
        title="Supprimer ce POI ?" testIdPrefix="poi-delete"
        description={toDelete ? `« ${toDelete.name} » sera définitivement supprimé. Refusé s'il est utilisé par un inter-arrêt ou une tournée (utilisez Désactiver).` : ""} />
      <AlertDialog open={!!pendingResolve} onOpenChange={(o) => !o && setPendingResolve(null)}>
        <AlertDialogContent className="border border-zinc-900 rounded-none" data-testid="poi-resolve-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight">Valider la coordonnée {pendingResolve?.label} ?</AlertDialogTitle>
            <AlertDialogDescription data-testid="poi-resolve-confirm-desc">
              {pendingResolve && <>« {pendingResolve.stop.name} » sera positionné en <b>{pendingResolve.candidate.latitude}, {pendingResolve.candidate.longitude}</b> et réactivé. Les deux candidats restent dans l'historique.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-zinc-900" data-testid="poi-resolve-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction className="rounded-none bg-zinc-900 text-white border border-zinc-900 hover:bg-zinc-800" data-testid="poi-resolve-confirm" onClick={(e) => { e.preventDefault(); confirmResolve(); }}>
              Valider cette position
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!pendingMove} onOpenChange={(o) => !o && cancelMove()}>
        <AlertDialogContent className="border border-zinc-900 rounded-none" data-testid="poi-move-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight">Confirmer le déplacement ?</AlertDialogTitle>
            <AlertDialogDescription data-testid="poi-move-confirm-desc">
              {pendingMove && <>« {pendingMove.stop.name} » : {formatCoord(pendingMove.stop.latitude)}, {formatCoord(pendingMove.stop.longitude)} → <b>{formatCoord(pendingMove.lat)}, {formatCoord(pendingMove.lng)}</b></>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-zinc-900" data-testid="poi-move-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction className="rounded-none bg-zinc-900 text-white border border-zinc-900 hover:bg-zinc-800" data-testid="poi-move-confirm" onClick={(e) => { e.preventDefault(); confirmMove(); }}>
              Enregistrer la nouvelle position
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageShell>
  );
}
